import type { StoreOrder } from "@medusajs/types";
import { cn } from "#/lib/utils";

type StageKey =
  | "placed"
  | "awaiting_payment"
  | "paid"
  | "fulfilling"
  | "shipped"
  | "delivered"
  | "cancelled";

const STAGE_LABEL: Record<StageKey, string> = {
  placed: "Order Placed",
  awaiting_payment: "Awaiting Payment",
  paid: "Payment Confirmed",
  fulfilling: "Preparing Fulfillment",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const STAGE_DOT: Record<StageKey, string> = {
  placed: "bg-lp-green text-white",
  awaiting_payment: "bg-lp-yellow text-lp-text-dk",
  paid: "bg-lp-green text-white",
  fulfilling: "bg-lp-green-mid text-white",
  shipped: "bg-lp-green-mid text-white",
  delivered: "bg-lp-green-dk text-white",
  cancelled: "bg-destructive text-destructive-foreground",
};

export type StatusTrackerProps = Pick<
  StoreOrder,
  "status" | "payment_status" | "fulfillment_status"
>;

function deriveStages(props: StatusTrackerProps): ReadonlyArray<StageKey> {
  const { status, payment_status, fulfillment_status } = props;
  if (status === "canceled") return ["placed", "cancelled"];

  const stages: StageKey[] = ["placed"];
  if (payment_status === "awaiting") stages.push("awaiting_payment");
  if (payment_status === "captured" || payment_status === "authorized") {
    stages.push("paid");
  }
  if (
    fulfillment_status === "fulfilled" ||
    fulfillment_status === "partially_fulfilled"
  ) {
    stages.push("fulfilling");
  }
  if (
    fulfillment_status === "shipped" ||
    fulfillment_status === "partially_shipped"
  ) {
    stages.push("fulfilling", "shipped");
  }
  if (fulfillment_status === "delivered") {
    stages.push("fulfilling", "shipped", "delivered");
  }
  return Array.from(new Set(stages));
}

export function StatusTracker(props: StatusTrackerProps) {
  const stages = deriveStages(props);
  return (
    <ol className="flex flex-col gap-6">
      {stages.map((stage) => (
        <li key={stage} className="flex items-start gap-5">
          <div
            className={cn(
              "mt-1 flex size-8 shrink-0 items-center justify-center rounded-full font-bold",
              STAGE_DOT[stage],
            )}
          >
            ✓
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold text-lg text-lp-text-dk">
              {STAGE_LABEL[stage]}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}
